import type { MeetingEndReason } from '@hub/contracts'

/**
 * The meeting session's state machine (board fqw7).
 *
 * The media session — the RealtimeKit client and the room socket — belongs
 * to the **project**, not to the `/meet/$code` route: navigating to a chat
 * or a document within the project keeps you in the meeting, the route is
 * only a view over the session, and the "ongoing meeting" card stands in
 * while the route is not on screen. Leaving the project, the Leave button
 * and the host's end are the only ways out; a reload reconnects.
 *
 * This file is the pure part: the reducer, the visibility rules and the
 * sessionStorage codec. The provider (`meeting-session-context.tsx`) owns
 * the SDK objects and dispatches into it.
 */

export type SessionStatus =
  /** No meeting. */
  | 'idle'
  /** The pre-join screen's Join was pressed; media is being published. */
  | 'joining'
  /** A reload or tab restore found a stored session and is re-joining it. */
  | 'reconnecting'
  /** In the meeting — on the route or not. */
  | 'active'
  /** Leave in progress: media stopped, socket closing, storage cleared. */
  | 'leaving'

/** What a reload needs to come back as the same participant. */
export interface StoredSession {
  v: 1
  projectId: string
  code: string
  /** The room socket's client id — the same across a reload. */
  clientId: string
  meeting: { id: string; title: string }
  self: { userId: string; name: string; isHost: boolean; canEnd: boolean }
  notesDocumentId: string | null
  /**
   * The RealtimeKit token minted by `meetings.join`. Add-participant is
   * idempotent per `custom_participant_id` and the token lives ~100 days,
   * so re-using it re-joins as the **same** participant. Null on the dev
   * path without RealtimeKit.
   */
  rtk: { authToken: string; meetingId: string } | null
  /** Restored after a reconnect, so a muted person comes back muted. */
  micOn: boolean
  cameraOn: boolean
}

export interface EndedInfo {
  code: string
  reason: MeetingEndReason
  notesDocumentId: string | null
}

export interface SessionState {
  status: SessionStatus
  /** The meeting this session is about; null while idle. */
  session: StoredSession | null
  /** The `/meet/$code` route for this session is on screen. */
  onRoute: boolean
  /** The last meeting that ended under us — the route shows the ended screen from it. */
  ended: EndedInfo | null
}

export const IDLE_SESSION: SessionState = {
  status: 'idle',
  session: null,
  onRoute: false,
  ended: null,
}

export type SessionEvent =
  | { type: 'join'; session: StoredSession }
  | { type: 'reconnect'; session: StoredSession }
  /** Media published (or nothing to publish): the session is live. */
  | { type: 'joined' }
  /** Joining or reconnecting failed; back to idle, the route decides what to show. */
  | { type: 'join-failed' }
  | { type: 'leave' }
  | { type: 'left' }
  | { type: 'ended'; reason: MeetingEndReason; notesDocumentId: string | null }
  | { type: 'route'; pathname: string }
  | { type: 'media'; micOn: boolean; cameraOn: boolean }

export function reduceSession(state: SessionState, event: SessionEvent): SessionState {
  switch (event.type) {
    case 'join':
      // A new meeting replaces whatever was there; the provider has already
      // left the old one.
      return { ...state, status: 'joining', session: event.session, ended: null }
    case 'reconnect':
      if (state.status !== 'idle') return state
      return { ...state, status: 'reconnecting', session: event.session, ended: null }
    case 'joined':
      if (state.status !== 'joining' && state.status !== 'reconnecting') return state
      return { ...state, status: 'active' }
    case 'join-failed':
      if (state.status !== 'joining' && state.status !== 'reconnecting') return state
      return { ...state, status: 'idle', session: null }
    case 'leave':
      if (state.status === 'idle' || state.status === 'leaving') return state
      return { ...state, status: 'leaving' }
    case 'left':
      if (state.status !== 'leaving') return state
      return { ...state, status: 'idle', session: null }
    case 'ended':
      if (state.status === 'idle' || !state.session) return state
      return {
        ...state,
        status: 'idle',
        session: null,
        ended: {
          code: state.session.code,
          reason: event.reason,
          notesDocumentId: event.notesDocumentId ?? state.session.notesDocumentId,
        },
      }
    case 'route': {
      const onRoute = state.session
        ? isMeetRoute(event.pathname, state.session.projectId, state.session.code)
        : false
      return onRoute === state.onRoute ? state : { ...state, onRoute }
    }
    case 'media':
      if (!state.session) return state
      if (state.session.micOn === event.micOn && state.session.cameraOn === event.cameraOn) {
        return state
      }
      return {
        ...state,
        session: { ...state.session, micOn: event.micOn, cameraOn: event.cameraOn },
      }
  }
}

/** Something is live (or coming back) — the provider holds SDK objects. */
export function isSessionLive(state: SessionState): boolean {
  return state.status === 'active' || state.status === 'reconnecting' || state.status === 'joining'
}

/** The "ongoing meeting" card: a live session whose route is not on screen. */
export function shouldShowOngoingCard(state: SessionState): boolean {
  return (state.status === 'active' || state.status === 'reconnecting') && !state.onRoute
}

/** Is `pathname` this meeting's own route? Trailing slashes tolerated. */
export function isMeetRoute(pathname: string, projectId: string, code: string): boolean {
  const clean = pathname.replace(/\/+$/, '')
  return clean === `/${projectId}/meet/${code}`
}

/**
 * Does navigating to `pathname` leave the project? Everything under
 * `/$projectId/…` (and the project root itself) stays; another project,
 * `/settings`, `/admin`, the project list — all leave.
 */
export function isProjectExit(projectId: string, pathname: string): boolean {
  const clean = pathname.replace(/\/+$/, '')
  return clean !== `/${projectId}` && !clean.startsWith(`/${projectId}/`)
}

// ── sessionStorage codec ──────────────────────────────────────────────────

export const SESSION_STORAGE_KEY = 'hub:meet:session'

interface StorageLike {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

/**
 * The stored session for this project, or null. A session of another
 * project is left alone — its own layout decides what to do with it.
 */
export function readStoredSession(storage: StorageLike, projectId: string): StoredSession | null {
  let raw: string | null
  try {
    raw = storage.getItem(SESSION_STORAGE_KEY)
  } catch {
    return null
  }
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as Partial<StoredSession>
    if (
      parsed.v !== 1 ||
      typeof parsed.projectId !== 'string' ||
      typeof parsed.code !== 'string' ||
      typeof parsed.clientId !== 'string' ||
      !parsed.meeting ||
      typeof parsed.meeting.id !== 'string' ||
      !parsed.self ||
      typeof parsed.self.userId !== 'string'
    ) {
      return null
    }
    if (parsed.projectId !== projectId) return null
    return {
      v: 1,
      projectId: parsed.projectId,
      code: parsed.code,
      clientId: parsed.clientId,
      meeting: { id: parsed.meeting.id, title: String(parsed.meeting.title ?? '') },
      self: {
        userId: parsed.self.userId,
        name: String(parsed.self.name ?? ''),
        isHost: Boolean(parsed.self.isHost),
        canEnd: Boolean(parsed.self.canEnd),
      },
      notesDocumentId: typeof parsed.notesDocumentId === 'string' ? parsed.notesDocumentId : null,
      rtk:
        parsed.rtk &&
        typeof parsed.rtk.authToken === 'string' &&
        typeof parsed.rtk.meetingId === 'string'
          ? { authToken: parsed.rtk.authToken, meetingId: parsed.rtk.meetingId }
          : null,
      micOn: Boolean(parsed.micOn),
      cameraOn: Boolean(parsed.cameraOn),
    }
  } catch {
    return null
  }
}

export function writeStoredSession(storage: StorageLike, session: StoredSession): void {
  try {
    storage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session))
  } catch {
    // Private mode or a full store: a reload will land on the pre-join screen.
  }
}

export function clearStoredSession(storage: StorageLike): void {
  try {
    storage.removeItem(SESSION_STORAGE_KEY)
  } catch {
    // Nothing to clear.
  }
}
